import {
  ApiResponseOfEOIApplicationsReponse,
  EOIApplicationsReponse,
  EOIApplicationsRequest,
  EoiStatus,
  FormsClient,
  SearchSortDirection,
  SearchSortField
} from '@benefitsui/app/api/benefits-clients';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { BehaviorSubject, combineLatest, interval, merge, Observable } from 'rxjs';
import { ExcelExportService } from '../../services/export/excel-export.service';
import { FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { debounce, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { DataTableHeaderOption, TableCell, TableData, TooltipContent } from '@dxo/ng-swift-ui';
import { EoiTableCell, EoiTableCellsData, ReportPageParameters } from './report-page.types';
import { PolicyService } from '../../services/policy/policy.service';
import { DataLabel } from '@benefitsui/app/shared/interfaces/IDataLabel';
import { Constants } from '../billing-page/billing-page.types';
import { Defaults } from '@benefitsui/app/shared/defaults/defaults';

@Component({
  selector: 'app-report-page',
  templateUrl: './report-page.component.html',
  styleUrls: ['./report-page.component.scss'],
  standalone: false
})
export class ReportPageComponent implements OnInit {
  FIELD_LIMIT = 100;
  NAME_MIN_LENGTH = 3;
  isLoading = true;
  submittedFromDateLabel = `Submitted from date (${Defaults.dateFormatStringUI})`;
  submittedToDateLabel = `Submitted to date (${Defaults.dateFormatStringUI})`;
  decisionFromDateLabel = `Decision from date (${Defaults.dateFormatStringUI})`;
  decisionToDateLabel = `Decision to date (${Defaults.dateFormatStringUI})`;

  totalResults = 10000;
  SortDirection = SearchSortDirection;
  private pageSize$ = new BehaviorSubject<number>(10);
  private page$ = new BehaviorSubject<number>(1);
  private sortDirection$ = new BehaviorSubject<SearchSortDirection>(SearchSortDirection.Descending);
  policyId$ = new BehaviorSubject<string>(Constants.StringEmpty);
  employeeName$ = new BehaviorSubject<string>(Constants.StringEmpty);
  applicantName$ = new BehaviorSubject<string>(Constants.StringEmpty);
  submittedFromDate$ = new BehaviorSubject<string>(Constants.StringEmpty);
  submittedToDate$ = new BehaviorSubject<string>(Constants.StringEmpty);
  decisionFromDate$ = new BehaviorSubject<string>(Constants.StringEmpty);
  decisionToDate$ = new BehaviorSubject<string>(Constants.StringEmpty);
  status$ = new BehaviorSubject<Array<string>>([]);
  sortField$ = new BehaviorSubject<SearchSortField>(SearchSortField.Name);
  applicants$: Observable<EoiTableCellsData | undefined>;
  //criteria for all results
  criteria: EOIApplicationsRequest = {
    sortDirection: SearchSortDirection.Descending,
    page: 1,
    pageSize: this.totalResults,
    sortField: SearchSortField.Name,
    policyId: undefined,
    policyIds: undefined,
    employeeName: undefined,
    applicantName: undefined,
    submittedFromDate: undefined,
    submittedToDate: undefined,
    decisionFromDate: undefined,
    decisionToDate: undefined,
    status: undefined
  };

  constructor(
    private excelExportService: ExcelExportService,
    private fb: UntypedFormBuilder,
    private policyService: PolicyService,
    private formsClient: FormsClient
  ) {
    // subscribe to the observables of all the param values. distinctUntilChanged to avoid unnecessary calls.
    // once a param changes, issue the new search api call to get the new data.
    this.applicants$ = combineLatest([
      this.page$,
      this.pageSize$.pipe(distinctUntilChanged()),
      this.sortDirection$.pipe(distinctUntilChanged()),
      this.sortField$.pipe(distinctUntilChanged()),
      this.policyId$.pipe(distinctUntilChanged())
    ]).pipe(
      switchMap((vals) => {
        this.searchParameter.page = vals[0];
        this.searchParameter.pageSize = vals[1];
        this.searchParameter.sortDirection = vals[2];
        this.searchParameter.sortField = vals[3];
        this.searchParameter.policyId = vals[4];

        this.searchParameter.status = this.selectedStatuses;
        this.searchParameter.employeeName = this.employeeName$.value ?? Constants.StringEmpty;
        this.searchParameter.applicantName = this.applicantName$?.value ?? Constants.StringEmpty;
        this.searchParameter.submittedFromDate = this.submittedFromDate$?.value ?? Constants.StringEmpty;
        this.searchParameter.submittedToDate = this.submittedToDate$?.value ?? Constants.StringEmpty;
        this.searchParameter.decisionFromDate = this.decisionFromDate$.value ?? Constants.StringEmpty;
        this.searchParameter.decisionToDate = this.decisionToDate$.value ?? Constants.StringEmpty;

        const criteria: EOIApplicationsRequest = {
          sortDirection: this.searchParameter.sortDirection,
          page: this.searchParameter.page,
          pageSize: this.searchParameter.pageSize,
          sortField: this.searchParameter.sortField,
          policyId: this.searchParameter.policyId ? this.searchParameter.policyId : undefined,
          policyIds: undefined,
          employeeName: this.searchParameter.employeeName ? this.searchParameter.employeeName : undefined,
          applicantName: this.searchParameter.applicantName ? this.searchParameter.applicantName : undefined,
          submittedFromDate: this.searchParameter.submittedFromDate
            ? this.searchParameter.submittedFromDate
            : undefined,
          submittedToDate: this.searchParameter.submittedToDate ? this.searchParameter.submittedToDate : undefined,
          decisionFromDate: this.searchParameter.decisionFromDate ? this.searchParameter.decisionFromDate : undefined,
          decisionToDate: this.searchParameter.decisionToDate ? this.searchParameter.decisionToDate : undefined,
          status: this.searchParameter.status?.length !== 0 ? this.searchParameter.status : undefined
        };

        return this.formsClient.eoireport(criteria).pipe(map((x) => x));
      }),
      map(this.transformData),
      map((x) => this.transformApplicants(x)),
      tap(() => (this.isLoading = false))
    );

    this.eoiApplicantForm = this.fb.group({
      employeeNameOrId: [
        Constants.StringEmpty,
        [Validators.required, Validators.minLength(this.NAME_MIN_LENGTH), Validators.maxLength(this.FIELD_LIMIT)]
      ],
      applicantName: [
        Constants.StringEmpty,
        [Validators.required, Validators.minLength(this.NAME_MIN_LENGTH), Validators.maxLength(this.FIELD_LIMIT)]
      ],
      submittedFromDate: [Constants.StringEmpty, [Validators.required]],
      submittedToDate: [Constants.StringEmpty, [Validators.required]],
      decisionFromDate: [Constants.StringEmpty, [Validators.required]],
      decisionToDate: [Constants.StringEmpty, [Validators.required]],
      status: [[], [Validators.required]]
    });
  }

  // :  Data members
  eoiApplicantForm: FormGroup;

  dateErrorMessage = 'The from date must occur before the to date';
  token = Constants.StringEmpty;
  dateFormatMessage = 'Date format must be mm/dd/yyyy';

  selectedStatuses: string[] = [];

  contentStatus: TooltipContent = {
    heading: 'EOI status key',
    body: `Approved: Application has been approved
    Declined: Application has been declined
    Pending: Application has been received and is being reviewed
    Incomplete: Application has been closed as incomplete or expired due to missing information not received
    EOI not needed: Evidence of Insurability Application was not needed for requested amount of coverage
    Returned to applicant: Application was returned to the applicant for missing information
    Returned to policyholder: A request for more information or confirmation has been sent to the policyholder
    Withdrew: Application was withdrawn at the applicant's requests`
  };

  toggleStatusSelection(status: string) {
    const index = this.selectedStatuses.indexOf(status);

    if (index === -1) {
      this.selectedStatuses.push(status);
    } else {
      this.selectedStatuses.splice(index, 1);
    }

    this.eoiApplicantForm.controls.status.setValue(this.selectedStatuses);
  }

  formatAmount(amount: number | undefined): string | null {
    if (amount === 0) return 'N/A';
    else {
      const currencyPipe = new CurrencyPipe('en-US');
      return currencyPipe.transform(amount);
    }
  }

  validateDateFormat(value: string): boolean {
    const date_regex = /^\d{2}\/\d{2}\/\d{4}$/;
    return date_regex.test(value) || value === Constants.StringEmpty;
  }

  toTitleCase(value: string | undefined): string | undefined {
    return value?.replace(/\w\S*/g, function (txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  }

  toSentenceCase(value: string | undefined): string | undefined {
    if (value) return value?.charAt(0).toUpperCase() + value?.slice(1);
    else return Constants.StringEmpty;
  }

  formatDate(date: string | undefined): string | null {
    const datePipe = new DatePipe('en-US');
    if (date === Constants.StringEmpty || date === null) return date;
    else return datePipe.transform(date, 'MM/dd/yyyy');
  }

  isSelected(status: string): boolean {
    return this.selectedStatuses.includes(status);
  }

  async submit() {
    console.log('Submitted');
  }

  clearFilters() {
    this.selectedStatuses = [];
    this.eoiApplicantForm.setValue({
      employeeNameOrId: Constants.StringEmpty,
      applicantName: Constants.StringEmpty,
      submittedFromDate: Constants.StringEmpty,
      submittedToDate: Constants.StringEmpty,
      decisionFromDate: Constants.StringEmpty,
      decisionToDate: Constants.StringEmpty,
      status: []
    });
  }

  transformData(response: ApiResponseOfEOIApplicationsReponse): EOIApplicationsReponse {
    if (response && response.data) {
      return {
        applicants: response.data.applicants?.length !== 0 ? response.data.applicants : [],
        resultsPerPage: response.data.resultsPerPage ? response.data.resultsPerPage : 0,
        currentPage: response.data.currentPage ? response.data.currentPage : 1,
        totalPages: response.data.totalPages ? response.data.totalPages : 1,
        totalResults: response.data.totalResults ? response.data.totalResults : 0,
        sortField: response.data.sortField ? response.data.sortField : SearchSortField.Name,
        sortDirection: response.data.sortDirection ? response.data.sortDirection : SearchSortDirection.Ascending
      };
    } else {
      // Handle the case where response or response.data is null or undefined
      return {
        applicants: [],
        resultsPerPage: 0,
        currentPage: 1,
        totalPages: 1,
        totalResults: 0,
        sortField: SearchSortField.Name,
        sortDirection: SearchSortDirection.Ascending
      };
    }
  }

  statusColors = {
    red: '#D32F2F',
    green: '#8ABF2E',
    yellow: '#FDD835'
  };

  getStatusColor(status: string | undefined): string {
    //returns status color
    switch (status?.toLocaleLowerCase()) {
      case EoiStatus.Approved.toLocaleLowerCase():
        return this.statusColors.green;
      case EoiStatus.Declined.toLocaleLowerCase():
        return this.statusColors.red;
      case EoiStatus.EOI_not_needed.toLocaleLowerCase():
        return this.statusColors.red;
      case EoiStatus.Expired_application.toLocaleLowerCase():
        return this.statusColors.red;
      case EoiStatus.Pending.toLocaleLowerCase():
        return this.statusColors.yellow;
      case EoiStatus.Returned_to_applicant.toLocaleLowerCase():
        return this.statusColors.yellow;
      case EoiStatus.Returned_to_policyholder.toLocaleLowerCase():
        return this.statusColors.yellow;
      case EoiStatus.Incomplete.toLocaleLowerCase():
        return this.statusColors.red;
      case EoiStatus.Withdrew.toLocaleLowerCase():
        return this.statusColors.red;
      default:
        return Constants.StringEmpty;
    }
  }

  transformApplicants(response: EOIApplicationsReponse | undefined): EoiTableCellsData | undefined {
    this.totalResults = response?.totalResults ?? 0;
    const eoiArr: EoiTableCell[] = [];
    response?.applicants?.map((app) => {
      eoiArr.push({
        employee: this.toTitleCase(app.employeeName),
        display: this.toTitleCase(app.employeeName),
        applicant: this.toTitleCase(app.applicantName),
        coverage: this.toSentenceCase(app.coverage),
        status: this.toSentenceCase(app.status),
        amount: this.formatAmount(app.amount),
        submitted: this.formatDate(app.submittedDate),
        decision: this.formatDate(app.decisionDate),
        indicator: this.getStatusColor(app.status)
      });
    });
    return {
      listEoi: eoiArr ?? [],
      totalResults: response?.totalResults ?? 0
    };
  }

  isEoiApplicantsFormsEmpty(): boolean {
    return (
      this.eoiApplicantForm.controls.employeeNameOrId.value === Constants.StringEmpty &&
      this.eoiApplicantForm.controls.applicantName.value === Constants.StringEmpty &&
      this.eoiApplicantForm.controls.submittedFromDate.value === Constants.StringEmpty &&
      this.eoiApplicantForm.controls.submittedToDate.value === Constants.StringEmpty &&
      this.eoiApplicantForm.controls.decisionFromDate.value === Constants.StringEmpty &&
      this.eoiApplicantForm.controls.decisionToDate.value === Constants.StringEmpty
    );
  }

  isDateGreaterThan(fromDate: string, toDate: string): boolean {
    const from = new Date(fromDate);
    const to = new Date(toDate);
    return from > to;
  }

  statusList = [
    'Approved',
    'Declined',
    'EOI not needed',
    'Incomplete',
    'Pending',
    'Returned to applicant',
    'Returned to policyholder',
    'Withdrew'
  ];

  applicantData: ApplicantData = {
    employeeNameOrId: {
      data: Constants.StringEmpty,
      label: 'Employee name',
      placeHolder: 'Employee name',
      tooltipContent: { heading: 'Employee', body: 'This is your employee.' }
    },
    applicantName: {
      data: Constants.StringEmpty,
      label: 'Applicant name',
      placeHolder: 'Applicant Name',
      tooltipContent: { heading: 'Applicant', body: 'Enter applicant name' }
    },
    status: { data: 'Active', label: 'Status' },
    submittedFromDate: {
      data: '01/01/2020',
      label: 'Submitted date range',
      placeHolder: `From Date (${Defaults.dateFormatStringUI})`,
      tooltipContent: { heading: 'Submitted Date Range', body: 'Enter date range' }
    },
    submittedToDate: { data: '01/01/2021', placeHolder: `To Date (${Defaults.dateFormatStringUI})` },
    decisionFromDate: {
      data: '01/01/2020',
      label: 'Decision date range',
      placeHolder: `From Date (${Defaults.dateFormatStringUI})`,
      tooltipContent: { heading: 'Decision date range', body: 'Enter date range' }
    },
    decisionToDate: { data: '01/01/2021', placeHolder: `To Date (${Defaults.dateFormatStringUI})` }
  };

  tableHeaders: DataTableHeaderOption[] = [
    { text: 'Employee', key: 'employee', width: '15%' },
    { text: 'Applicant', key: 'applicant', width: '15%' },
    { text: 'Coverage', key: 'coverage', width: '20%' },
    { text: 'Status', key: 'status', width: '15' },
    { text: 'Amount', key: 'amount', width: '15' },
    { text: 'Submitted', key: 'submitted' },
    { text: 'Decision', key: 'decision' }
  ];

  getStatusE2E(status: string): string {
    return 'status-value-' + status.replace(/[^a-zA-Z]/g, '').toLowerCase();
  }  

  tableCells: TableCell[][] = [];

  onPageChange(page: number) {
    this.page$.next(page);
  }

  onPageSizeChange(pageSize: number) {
    this.pageSize$.next(pageSize);
  }

  onClickTableHeader(sortField: string) {
    let newSortDirection = SearchSortDirection.Descending;
    if (this.searchParameter.sortDirection === SearchSortDirection.Descending) {
      newSortDirection = SearchSortDirection.Ascending;
    }

    const field: SearchSortField = SearchSortField[sortField as keyof typeof SearchSortField];

    this.sortDirection$.next(newSortDirection);
    this.sortField$.next(field);
  }

  async fillExportTable() {
    this.formsClient
      .eoireport(this.criteria)
      .pipe(
        map(this.transformData),
        map((x) => this.transformApplicants(x))
      )
      .subscribe((data) => {
        if (data?.listEoi) {
          this.tableCells = data.listEoi.map((eoi) => [
            { data: eoi.employee ?? Constants.StringEmpty },
            { data: eoi.applicant ?? Constants.StringEmpty },
            { data: eoi.coverage ?? Constants.StringEmpty },
            { data: eoi.status ?? Constants.StringEmpty },
            { data: eoi.amount !== null ? eoi.amount.toString() : Constants.StringEmpty },
            { data: eoi.submitted !== null ? eoi.submitted.toString() : Constants.StringEmpty },
            { data: eoi.decision !== null ? eoi.decision.toString() : Constants.StringEmpty }
          ]);
        } else {
          this.tableCells = [];
        }
      });
  }

  exportTableToXLSX() {
    const tableData: TableData = {
      headers: this.tableHeaders,
      cells: this.tableCells
    };
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const day = String(currentDate.getDate()).padStart(2, '0');

    const datestamp = `${year}.${month}.${day}`;
    const filename = `EOI Report - ${this.policyService.groupId} - ${datestamp}.xlsx`;
    this.excelExportService.exportTableToXLSX(filename, tableData);
  }

  searchParameter: ReportPageParameters = {
    policyId: undefined,
    employeeName: undefined,
    applicantName: undefined,
    submittedFromDate: undefined,
    submittedToDate: undefined,
    decisionFromDate: undefined,
    decisionToDate: undefined,
    status: undefined,
    page: 1,
    pageSize: 10,
    sortField: SearchSortField.Name,
    sortDirection: SearchSortDirection.Ascending
  };

  ngOnInit(): void {
    //For Filtering- This piece detects any changes on the eoiApplicantForm attributes and subscribes to the changes.
    //Constructor is responsible for reading the changes nad calling new API
    this.fillExportTable().then(() => {
      if (this.eoiApplicantForm) {
        const employeeNameOrIdControl = this.eoiApplicantForm.get('employeeNameOrId');
        const applicantControl = this.eoiApplicantForm.get('applicantName');
        const submittedFromDate = this.eoiApplicantForm.get('submittedFromDate');
        const submittedToControl = this.eoiApplicantForm.get('submittedToDate');
        const decisionFromControl = this.eoiApplicantForm.get('decisionFromDate');
        const decisionToControl = this.eoiApplicantForm.get('decisionToDate');
        const statusControl = this.eoiApplicantForm.get('status');

        employeeNameOrIdControl?.valueChanges.subscribe((value) => {
          this.employeeName$.next(value);
        });

        applicantControl?.valueChanges.subscribe((value) => {
          this.applicantName$.next(value);
        });

        submittedFromDate?.valueChanges.subscribe((value) => {
          if (this.validateDateFormat(this.eoiApplicantForm.controls.submittedFromDate.value)) {
            this.submittedFromDate$.next(value);
          }
        });

        submittedToControl?.valueChanges.subscribe((value) => {
          if (this.validateDateFormat(this.eoiApplicantForm.controls.submittedToDate.value)) {
            this.submittedToDate$.next(value);
          }
        });

        decisionFromControl?.valueChanges.subscribe((value) => {
          if (this.validateDateFormat(this.eoiApplicantForm.controls.decisionFromDate.value)) {
            this.decisionFromDate$.next(value);
          }
        });

        decisionToControl?.valueChanges.subscribe((value) => {
          if (this.validateDateFormat(this.eoiApplicantForm.controls.decisionToDate.value)) {
            this.decisionToDate$.next(value);
          }
        });

        statusControl?.valueChanges.subscribe((value: string[]) => {
          this.status$.next(value);
          this.page$.next(1);
        });
      }
    });

    // when any of the following change, we need to reset
    // to the first page
    merge(
      this.employeeName$,
      this.applicantName$,
      this.submittedFromDate$,
      this.submittedToDate$,
      this.decisionFromDate$,
      this.decisionToDate$
    )
      .pipe(debounce(() => interval(250)))
      .subscribe(() => this.page$.next(1));
  }
}

export interface ApplicantData {
  employeeNameOrId: DataLabel;
  applicantName: DataLabel;
  status: DataLabel;
  submittedFromDate: DataLabel;
  submittedToDate: DataLabel;
  decisionFromDate: DataLabel;
  decisionToDate: DataLabel;
}
